<template>
    <sy-resize-panel ref="container" class="section" @resize="handleResize">
        <slot></slot>
    </sy-resize-panel>
</template>

<script>
import SyResizePanel from '@/components/ResizePanel/index';
export default {
    name: 'SySection',
    components: { SyResizePanel },
    provide() {
        return {
            section: this
        };
    },
    props: {
        /**
         * 布局模式
         */
        layoutMode: {
            type: String,
            default: 'space-between',
            validator: value => {
                // 值必须匹配下列字符串中的一个
                return ['flex-start', 'space-between'].indexOf(value) !== -1;
            }
        },
        /**
         * 列间距
         * 只有layoutMode为space-between发挥作用
         */
        colGap: {
            type: Number,
            default: 20
        },
        /**
         * 行间距
         * 只有存在换行的情况下发挥作用
         */
        rowGap: {
            type: Number,
            default: 10
        },
        /**
         * 标签位置
         */
        labelPosition: {
            type: String,
            default: 'right',
            validator: value => {
                // 值必须匹配下列字符串中的一个
                return ['left', 'right', 'top'].indexOf(value) !== -1;
            }
        },
        /**
         * 标签垂直对齐方式
         * 当labelPosition为top时无效
         */
        labelAlign: {
            type: String,
            default: 'flex-start',
            validator: value => {
                // 值必须匹配下列字符串中的一个
                return ['flex-start', 'center', 'flex-end'].indexOf(value) !== -1;
            }
        },
        /**
         * 子项宽度
         */
        itemWidth: {
            type: String,
            default: 'auto'
        }
    },
    data() {
        return {
            // 总宽度
            totalWidth: 0,
            // 子项列表
            itemList: []
        };
    },
    computed: {
        /**
         * 计算最大子项宽度
         */
        maxItemWidth() {
            let res = 0;
            this.itemList.forEach(item => {
                res = Math.max(res, item.itemWidth);
            });
            return res;
        },
        /**
         * 计算子项最大标签宽度
         */
        maxLabelWidth() {
            let res = 0;
            this.itemList.forEach(item => {
                res = Math.max(res, item.labelWidth);
            });
            return res;
        },
        /**
         * 计算子项最大内容宽度
         */
        maxContentWidth() {
            let res = 0;
            this.itemList.forEach(item => {
                res = Math.max(res, item.contentWidth);
            });
            return res;
        },
        /**
         * 计算每行子项的最大个数
         */
        itemCountOfLine() {
            // 计算每行子项的最大个数
            return Math.floor(this.totalWidth / this.maxItemWidth);
        },
        /**
         * 计算子项之间的间隙
         */
        autoColGap() {
            if (!this.itemList.length) {
                return 0;
            }

            if (!this.totalWidth) {
                return 0;
            }

            let gap = 0;
            if (this.itemCountOfLine <= 1) {
                gap = 0;
            } else if (this.itemCountOfLine > 1) {
                // 有可能子项个数排不满一整行,所以要取两者最小值进行计算
                const count = Math.min(this.itemList.length, this.itemCountOfLine);
                gap = (this.totalWidth - count * this.maxItemWidth) / (count - 1);
            }
            return gap;
        }
    },
    /**
     * 组件渲染完成方法
     */
    mounted() {
        this.updateTotalWidth();
        this.updateItemColGap();
    },
    /**
     * 组件更新方法
     */
    updated() {
        this.updateTotalWidth();
        this.updateItemColGap();
    },
    /**
     * 组件销毁方法
     */
    destroyed() {
        this.itemList = [];
    },
    methods: {
        /**
         * 更新总宽度
         */
        updateTotalWidth() {
            if (!this.$refs.container || !this.$refs.container.$el) {
                return 0;
            }

            const rect = this.$refs.container.$el.getBoundingClientRect();
            const style = window.getComputedStyle(this.$refs.container.$el, null);
            const paddingLeft = parseInt(style.paddingLeft.substring(0, style.paddingLeft.length - 2), 10);
            const paddingRight = parseInt(style.paddingRight.substring(0, style.paddingRight.length - 2), 10);
            // totalWidth = rect.width - paddingLeft - paddingRight;
            this.totalWidth = rect.width - paddingLeft - paddingRight;
        },
        /**
         * 添加子项
         * @param item
         */
        addItem(item) {
            this.itemList.push(item);
        },
        /**
         * 删除子项
         * @param target
         */
        removeItem(target) {
            const index = this.itemList.findIndex(item => item === target);
            this.itemList.splice(index, 1);
        },
        /**
         * 更新子项间距
         */
        updateItemColGap() {
            if (!this.itemList.length) {
                return;
            }
            const rows = Math.ceil(this.itemList.length / this.itemCountOfLine);

            this.itemList.forEach((item, index) => {
                let marginRight = this.layoutMode === 'flex-start' ? this.colGap : this.autoColGap;
                let marginBottom = this.rowGap;
                // 每行的最后一个和最后一个item的marginRight设置为0
                if ((index + 1) % this.itemCountOfLine === 0 || index === this.itemList.length - 1) {
                    marginRight = 0;
                }
                // 在最后一行item的marginBottom设置为0
                if (Math.ceil((index + 1) / this.itemCountOfLine) === rows) {
                    marginBottom = 0;
                }
                item.setColGap(marginRight);
                item.setRowGap(marginBottom);
            });
        },
        /**
         * 处理尺寸变化
         * @param rect
         */
        handleResize(rect) {
            this.updateTotalWidth();
            this.updateItemColGap();
        }
    }
};
</script>

<style lang="scss" scoped>
.section {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    padding: 0 48px;
    font-size: 14px;
    line-height: 32px;
}
</style>
